/**
 * AppV2 - New tab-based application shell
 * Replaces the old hierarchical navigation with a 5-tab Bottom Navigation Bar
 *
 * Preserves from old App:
 * - Telegram auto-registration
 * - useLoadPreferences (cross-device sync)
 * - Deep link handling (org_invite_)
 * - QueryClientProvider
 */

import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { useTabBackButton } from './hooks/useTabBackButton';
import { useLoadPreferences } from './hooks/useLoadPreferences';
import { useSubscriptionInfo } from './hooks/useSubscriptionInfo';
import { useAuthStore } from './store/authStore';
import { useTabNavigationStore } from './store/tabNavigationStore';
import { useThemeStore } from './store/themeStore';
import { usersApi } from './api/users';

// Layout
import { AppShell } from './components/ui/AppShell';

// Tab renderers
import {
  renderHomeStack,
  renderAthletesStack,
  renderGroupsStack,
  renderProfileStack,
} from './navigation/tabRenderers';

// Bottom sheet content
import { AIAssistantSheet } from './screens/Hub/AIAssistantSheet';
import { OrgSwitcherSheet } from './screens/Sheets/OrgSwitcherSheet';
import { DirectionSwitcherSheet } from './screens/Sheets/DirectionSwitcherSheet';

// QueryClient singleton
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Сетевые ошибки — пробуем до 3 раз с задержкой
        const isNetworkError =
          error && typeof error === 'object' && 'error' in error &&
          (error as { error?: string }).error === 'Network error';
        if (isNetworkError) return failureCount < 3;
        // Серверные ошибки (5xx) — 1 retry
        // Клиентские ошибки (4xx) — без retry
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Не показывать старые ошибочные данные при reconnect
      refetchOnReconnect: true,
    },
  },
});

function AppV2Content() {
  const { user: telegramUser } = useTelegramWebApp();
  const { user, setUser } = useAuthStore();
  const { activeTab, getCurrentScreen, switchTab, pushScreen } = useTabNavigationStore();
  const { isDark, setTheme } = useThemeStore();

  // Telegram BackButton integration
  useTabBackButton();

  // Load preferences from server on startup
  const { isLoading: isLoadingPreferences } = useLoadPreferences();

  // Load subscription info (limits, usage, grace status) into authStore
  useSubscriptionInfo();

  // Initialize theme on mount
  useEffect(() => {
    setTheme(isDark);
  }, []);

  // Auto-registration via Telegram
  useEffect(() => {
    const isValidUser = user && user.id;

    if (telegramUser && !isValidUser) {
      usersApi
        .register({
          telegram_user_id: telegramUser.id,
          telegram_username: telegramUser.username,
          name:
            telegramUser.first_name +
            (telegramUser.last_name ? ` ${telegramUser.last_name}` : ''),
        })
        .then((registeredUser) => {
          setUser(registeredUser);
        })
        .catch((error) => {
          console.error('Failed to register user:', error);
        });
    }
  }, [telegramUser, user, setUser]);

  // Deep link handling (org_invite_)
  useEffect(() => {
    if (!user) return;
    if (isLoadingPreferences) return;

    const hash = window.location.hash.slice(1);

    console.log('[DeepLink] Checking for invite code...', {
      hash,
      user: user?.id,
    });

    if (hash && hash.startsWith('org_invite_')) {
      const fullCode = hash.replace('org_invite_', '');
      const inviteCode = fullCode.split('?')[0];

      console.log(
        '[DeepLink] ✅ Invite code detected, navigating to join-organization:',
        inviteCode,
      );

      window.location.hash = '';

      // Navigate to Profile tab → push join-organization screen
      switchTab('profile');
      pushScreen('join-organization', { inviteCode });
      return;
    } else {
      console.log('[DeepLink] No invite code found in URL hash');
    }
  }, [user, switchTab, pushScreen, isLoadingPreferences]);

  // Render active tab content
  const currentScreen = getCurrentScreen();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHomeStack(currentScreen);
      case 'athletes':
        return renderAthletesStack(currentScreen);
      case 'groups':
        return renderGroupsStack(currentScreen);
      case 'profile':
        return renderProfileStack(currentScreen);
      default:
        return renderHomeStack(currentScreen);
    }
  };

  // Show loading until user is validated
  const isValidUser = user && user.id;

  if (!isValidUser) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  return (
    <AppShell
      hubContent={<AIAssistantSheet />}
      orgSwitcherContent={<OrgSwitcherSheet />}
      directionSwitcherContent={<DirectionSwitcherSheet />}
    >
      {renderTabContent()}
    </AppShell>
  );
}

function AppV2() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppV2Content />
    </QueryClientProvider>
  );
}

export default AppV2;
